/**
 * Personal MCP Contracts
 *
 * Contracts for the personal/account-level MCP servers DataTable.
 * These are for users viewing their own MCP servers (without organization context).
 *
 * For org-level MCP contracts, see ./mcp.ts
 */
import { z } from 'zod';
import { paginationSchema, sortOrderSchema } from './helpers';
import { knownTotalParamsSchema, knownTotalQuerySchema } from './known-total';
import { ColumnDataTypes, FilterOperators, FiltersSchema, TableResponseMetaSchema, createTableQuerySchema, } from './table';
import { PLATFORM_IDS } from './platform-registry';
import { mcpServerListItemSchema, clientTypeSchema } from './mcp';
// Re-export shared types for convenience
export { mcpServerListItemSchema };
/**
 * Sort field options for personal MCP server list.
 * Duplicated here to avoid circular dependency with mcp.ts when both are
 * imported by api/mcp.ts.
 */
export const personalMcpSortFieldSchema = z.enum([
    'name',
    'version',
    'publisher',
    'type',
    'transport',
    'sources',
    'userCount',
    'runningCount',
    'tools',
    'firstSeenAt',
    'lastSeenAt',
]);
/**
 * Valid values for the runningCount (status) column filter.
 * "running" = runningCount > 0; "stopped" = runningCount === 0.
 */
export const mcpStatusFilterValueSchema = z.enum(['running', 'stopped']);
/**
 * Display labels for the personal MCP status (runningCount) column. Single
 * source of truth shared by the frontend filter options and the backend search
 * builder (matches the display label by partial substring).
 */
export const personalStatusLabels = {
    running: 'Active',
    stopped: 'Inactive',
};
/**
 * Valid values for the sources column filter.
 * Each value corresponds to a ClientType (IDE/agent platform).
 */
export const mcpSourceFilterValueSchema = clientTypeSchema;
// ============================================================================
// Personal MCP DataTable-specific schemas
// ============================================================================
/**
 * Filterable fields for the personal MCP servers DataTable.
 * Personal-level has SOURCES and RUNNING_COUNT instead of USERS and STATUS.
 */
export const PERSONAL_MCP_FILTERABLE_FIELDS = {
    NAME: 'name',
    VERSION: 'version',
    PUBLISHER: 'publisher',
    TYPE: 'type',
    TRANSPORT: 'transport',
    SOURCES: 'sources',
    TOOLS: 'tools',
    RUNNING_COUNT: 'runningCount',
    FIRST_SEEN_AT: 'firstSeenAt',
    LAST_SEEN_AT: 'lastSeenAt',
};
/**
 * Per-column operator allow-list for personal MCP filters. Shared by the
 * API-client params schema and the backend querystring schema so both layers
 * agree on which operators each column accepts.
 */
export const PERSONAL_MCP_OPERATOR_OVERRIDES = {
    [PERSONAL_MCP_FILTERABLE_FIELDS.FIRST_SEEN_AT]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [PERSONAL_MCP_FILTERABLE_FIELDS.LAST_SEEN_AT]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
};
/**
 * Shared refinement function to validate personal MCP-specific filter values.
 * Validates status (runningCount) and sources filter values, and enforces
 * per-column operator overrides.
 *
 * Use with `.superRefine()` on any schema that includes personal MCP filters.
 *
 * @param filters - Array of filter values (can be undefined)
 * @param ctx - Zod refinement context for adding issues
 */
export function validatePersonalMCPFilters(filters, ctx) {
    const overrides = PERSONAL_MCP_OPERATOR_OVERRIDES;
    filters?.forEach((filter, index) => {
        const override = overrides[filter.field];
        if (override && !override.includes(filter.operator)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['filters', index, 'operator'],
                message: `Operator "${filter.operator}" is not allowed for field "${filter.field}". Allowed operators: ${override.join(', ')}.`,
            });
        }
    });
    const statusFilter = filters?.find(f => f.field === PERSONAL_MCP_FILTERABLE_FIELDS.RUNNING_COUNT);
    if (statusFilter) {
        for (const val of statusFilter.value) {
            if (!mcpStatusFilterValueSchema.safeParse(val).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['filters'],
                    message: `Invalid status filter value "${val}". Must be "running" or "stopped".`,
                });
            }
        }
    }
    const sourcesFilter = filters?.find(f => f.field === PERSONAL_MCP_FILTERABLE_FIELDS.SOURCES);
    if (sourcesFilter) {
        for (const val of sourcesFilter.value) {
            if (!mcpSourceFilterValueSchema.safeParse(val).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['filters'],
                    message: `Invalid sources filter value "${val}". Must be one of: ${PLATFORM_IDS.join(', ')}.`,
                });
            }
        }
    }
}
/**
 * Query parameters for the personal MCP servers DataTable API call.
 * Accepts pre-parsed filters array (not bracket notation).
 */
export const listPersonalMcpServersDataTableParamsSchema = z
    .object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    filters: FiltersSchema.optional(),
    sortBy: personalMcpSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    knownTotal: knownTotalParamsSchema,
})
    .superRefine((data, ctx) => validatePersonalMCPFilters(data.filters, ctx));
/**
 * Backend query schema for personal MCP DataTable — handles bracket-notation filter
 * params from the query string OR a pre-parsed filters array.
 */
const personalMcpTableQueryBaseSchema = paginationSchema.extend({
    sortBy: personalMcpSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    search: z.string().optional(),
    knownTotal: knownTotalQuerySchema,
});
export const personalMcpTableQuerySchema = createTableQuerySchema(personalMcpTableQueryBaseSchema, {
    [PERSONAL_MCP_FILTERABLE_FIELDS.NAME]: ColumnDataTypes.STRING,
    [PERSONAL_MCP_FILTERABLE_FIELDS.VERSION]: ColumnDataTypes.STRING,
    [PERSONAL_MCP_FILTERABLE_FIELDS.PUBLISHER]: ColumnDataTypes.STRING,
    [PERSONAL_MCP_FILTERABLE_FIELDS.TYPE]: ColumnDataTypes.ENUM,
    [PERSONAL_MCP_FILTERABLE_FIELDS.TRANSPORT]: ColumnDataTypes.ENUM,
    [PERSONAL_MCP_FILTERABLE_FIELDS.SOURCES]: ColumnDataTypes.ENUM,
    [PERSONAL_MCP_FILTERABLE_FIELDS.TOOLS]: ColumnDataTypes.ENUM,
    [PERSONAL_MCP_FILTERABLE_FIELDS.RUNNING_COUNT]: ColumnDataTypes.ENUM,
    [PERSONAL_MCP_FILTERABLE_FIELDS.FIRST_SEEN_AT]: ColumnDataTypes.DATE,
    [PERSONAL_MCP_FILTERABLE_FIELDS.LAST_SEEN_AT]: ColumnDataTypes.DATE,
}, PERSONAL_MCP_OPERATOR_OVERRIDES).superRefine((data, ctx) => validatePersonalMCPFilters(data.filters, ctx));
/**
 * Response schema for personal MCP DataTable endpoint.
 * Shape: { data: MCPServerListItem[], pagination: {...}, meta: {...} }
 */
export const personalMcpTableResponseSchema = z.object({
    data: z.array(mcpServerListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
/**
 * Fields that support server-side filter option lookups for personal MCP servers.
 * Includes dynamic DB-backed fields (name, version, publisher, tools) and static
 * enum fields (type, transport, sources). tools serves distinct tool names from
 * each group's latest report.
 */
export const personalMcpFilterOptionFieldSchema = z.enum([
    PERSONAL_MCP_FILTERABLE_FIELDS.NAME,
    PERSONAL_MCP_FILTERABLE_FIELDS.VERSION,
    PERSONAL_MCP_FILTERABLE_FIELDS.PUBLISHER,
    PERSONAL_MCP_FILTERABLE_FIELDS.TYPE,
    PERSONAL_MCP_FILTERABLE_FIELDS.TRANSPORT,
    PERSONAL_MCP_FILTERABLE_FIELDS.SOURCES,
    PERSONAL_MCP_FILTERABLE_FIELDS.TOOLS,
]);
/**
 * Query schema for fetching personal MCP filter options.
 */
export const personalMcpFilterOptionsQuerySchema = z.object({
    field: personalMcpFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
/**
 * Response schema for personal MCP filter options.
 */
export const personalMcpFilterOptionsResponseSchema = z.object({
    field: personalMcpFilterOptionFieldSchema,
    options: z.array(z.string()),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
//# sourceMappingURL=account-mcp.js.map